/**
 * FAQ — questions/réponses BCL Telecom & Énergie.
 * Catégories : Raccordement / Tarifs / Documents / Espace client / Copropriété.
 */
import { HelpCircle, Mail, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MarketingLayout } from '@/components/site/marketing-layout';

type FaqEntry = { question: string; answer: string };

const sections: { title: string; entries: FaqEntry[] }[] = [
  {
    title: 'Raccordement & travaux',
    entries: [
      {
        question: "Quels sont les délais d'un raccordement fibre ?",
        answer:
          "En moyenne 2 à 8 semaines selon la complexité du génie civil, la disponibilité de l'opérateur et la zone d'éligibilité. Pour les projets neufs (permis récent), comptez 4 à 6 semaines une fois le dossier complet.",
      },
      {
        question: 'Mon adresse est-elle éligible à la fibre ?',
        answer:
          "Nous interrogeons les bases ARCEP et opérateurs (Orange, SFR, Free) lors de la qualification de votre demande. Si votre logement n'est pas encore éligible, nous montons le dossier de raccordement fibre auprès de l'opérateur d'aménagement de votre commune.",
      },
      {
        question: 'Qui pose la fibre dans mon logement ?',
        answer:
          "Le tirage final est réalisé par l'opérateur que vous aurez choisi (Orange, SFR, Free, Bouygues). Nous coordonnons le rendez-vous et nous assurons que l'infrastructure du logement et le branchement extérieur sont conformes.",
      },
      {
        question: 'Êtes-vous référencés ARCEP ?',
        answer:
          "Oui. BCL Telecom & Énergie est officiellement référencé par l'Autorité de Régulation des Communications Électroniques, des Postes et de la distribution de la presse. Ce référencement nous habilite à monter les dossiers de raccordement collectif et à intervenir sur les infrastructures fibre.",
      },
    ],
  },
  {
    title: 'Tarifs & paiement',
    entries: [
      {
        question: "Comment se déroule le paiement de l'offre ?",
        answer:
          "Deux options : carte bancaire (paiement immédiat sécurisé via notre prestataire FAM) ou virement bancaire (validation manuelle sous 24 h ouvrées avec envoi de RIB depuis votre espace client).",
      },
      {
        question: 'Quels packs proposez-vous ?',
        answer:
          "Pour les particuliers : Sérénité Fibre 340 € (raccordement maison individuelle) et Sérénité Premium 490 € (avec visite technique). Pour les pros et promoteurs : Essentiel Fibre 529 € et Confort Fibre 799 €.",
      },
      {
        question: 'Y a-t-il des frais cachés ?',
        answer:
          "Non. Le pack que vous payez couvre l'ensemble de notre prestation BCL (étude, montage, suivi, PV). Les éventuels travaux de génie civil sur domaine public ou les frais opérateur sont chiffrés à part et toujours validés avec vous avant exécution.",
      },
      {
        question: 'Puis-je obtenir un devis avant de m\'engager ?',
        answer:
          "Oui. Créez un compte, déclarez votre projet (adresse + permis de construire le cas échéant) et nous revenons sous 24 h ouvrées avec une qualification et un devis personnalisé. Aucun engagement avant signature.",
      },
    ],
  },
  {
    title: 'Documents & dossier',
    entries: [
      {
        question: 'Quels documents dois-je fournir ?',
        answer:
          "Pièce d'identité, justificatif de domicile, permis de construire (si projet neuf), plan cadastral, plan de situation et plan de masse, attestation de propriété. Pour les pros et promoteurs : extrait Kbis et tous documents techniques. Liste complète dans votre espace client.",
      },
      {
        question: 'Où téléverser mes documents ?',
        answer:
          "Depuis votre espace client, onglet Documents. Chaque pièce reçoit un statut (Non soumis / En attente / Validé / Refusé) et vous recevez une notification pour tout changement.",
      },
      {
        question: 'Que se passe-t-il si un document est refusé ?',
        answer:
          "Vous recevez un email avec le motif de refus. Vous pouvez retéléverser une version corrigée depuis l'espace client sans bloquer le reste du dossier.",
      },
    ],
  },
  {
    title: 'Espace client',
    entries: [
      {
        question: 'Comment suivre mon dossier en temps réel ?',
        answer:
          "Votre espace client affiche la timeline des 8 étapes : dépôt du dossier, vérification des documents, étude technique, demande opérateur, validation du devis, planification, réalisation, raccordement final. Chaque étape passe en vert avec sa date de complétion.",
      },
      {
        question: 'Puis-je avoir plusieurs projets en parallèle ?',
        answer:
          "Oui. Chaque projet (adresse + permis) est traité séparément avec ses propres documents, jalons et factures.",
      },
      {
        question: 'Comment contacter mon interlocuteur dédié ?',
        answer:
          "Depuis l'onglet Assistance de votre espace client : message direct, ouverture de ticket ou demande de rappel sous 4 h ouvrées.",
      },
    ],
  },
  {
    title: 'Copropriétés & collectifs',
    entries: [
      {
        question: "J'habite en copropriété, puis-je faire raccorder seul ?",
        answer:
          "Pour un raccordement individuel en collectif déjà fibré, oui. Si l'immeuble n'est pas encore raccordé, le syndic doit accepter la convention de raccordement. Nous accompagnons les syndics pour le vote en AG.",
      },
      {
        question: 'Travaillez-vous avec les syndics et promoteurs ?',
        answer:
          "Oui, sur l'ensemble du parcours : étude de faisabilité, dossier opérateur, supervision chantier, PV de réception. Tarification adaptée au volume.",
      },
      {
        question: 'Une procuration est-elle nécessaire ?',
        answer:
          'Uniquement si vous mandatez BCL Telecom pour vous représenter face à un opérateur ou un syndic. Modèle de procuration fourni dans votre espace client.',
      },
    ],
  },
];

export function FaqPage() {
  return (
    <MarketingLayout>
      {/* HERO */}
      <section className="bg-gradient-to-b from-white to-bcl-grey/40 py-20">
        <div className="container px-4 mx-auto">
          <div className="mx-auto max-w-3xl text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-bcl-blue mb-4">
              <HelpCircle className="h-3.5 w-3.5" />
              Aide & support
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-balance">
              Foire aux questions
            </h1>
            <p className="text-lg text-gray-600">
              Les réponses aux questions les plus fréquentes sur le
              raccordement fibre, nos packs et le suivi de votre dossier.
            </p>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-16 bg-white">
        <div className="container px-4 mx-auto max-w-4xl space-y-12">
          {sections.map((s) => (
            <div key={s.title}>
              <h2 className="text-2xl font-bold mb-4">{s.title}</h2>
              <Card>
                <CardContent className="px-6 py-2">
                  <Accordion type="single" collapsible className="w-full">
                    {s.entries.map((e, idx) => (
                      <AccordionItem
                        key={e.question}
                        value={`${s.title}-${idx}`}
                      >
                        <AccordionTrigger className="text-left">
                          {e.question}
                        </AccordionTrigger>
                        <AccordionContent>{e.answer}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-bcl-grey">
        <div className="container px-4 mx-auto max-w-4xl">
          <div className="rounded-2xl bg-bcl-blue p-10 text-white">
            <div className="grid gap-6 md:grid-cols-3 md:items-center">
              <div className="md:col-span-2">
                <h2 className="text-2xl font-bold mb-2 text-balance">
                  Vous n&apos;avez pas trouvé votre réponse&nbsp;?
                </h2>
                <p className="text-white/85">
                  Notre équipe répond sous 24 h ouvrées. Décrivez votre projet,
                  nous revenons vers vous avec une qualification précise.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <Link to="/contact">
                  <Button size="lg" variant="secondary" className="w-full">
                    <Mail className="mr-2 h-4 w-4" />
                    Nous contacter
                  </Button>
                </Link>
                <a href="tel:+33100000000">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full bg-transparent border-white text-white hover:bg-white/10"
                  >
                    <Phone className="mr-2 h-4 w-4" />
                    +33 1 00 00 00 00
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
